import React, { useState } from "react";

// Modules
import { useSelector } from "react-redux";
import moment from "moment";

// App
import { serverUrl } from "../../config";
import MediaLibrary from "../../partials/mediaLibrary";
import CategorySelector from "../../partials/categorySelector";
import SectionBuilder from "./sectionBuilder";
import MilestoneBuilder from "./milestoneBuilder";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendLinkIcon from "@mui/icons-material/ForwardToInboxOutlined";

const ROOM_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
];

const ALERT_MODES = [
  { value: "off", label: "Off" },
  { value: "first_open", label: "First visit only" },
  { value: "every_open", label: "Every visit" },
];

const ACCESS_MODES = [
  { value: "link", label: "Anyone with the link" },
  { value: "password", label: "Anyone with the link + passcode" },
  { value: "restricted", label: "Invited people only" },
];

// Single-line input sizing, mirrors addPresentation.js.
const inputSx = {
  "& .MuiInputBase-input": {
    height: 28,
  },
};

/**
 * Hydrates form-friendly sections from the API row: sections carry
 * {target_type, target_id} refs; field_references carries the resolved
 * titles/thumbnails. Stale refs (deleted content) are dropped.
 */
export const sectionsFromRoom = (room) => {
  const resolved = room.field_references || [];
  const byKey = {};
  resolved.forEach((ref) => {
    byKey[`${ref.type}:${ref.id}`] = ref;
  });

  return (room.field_sections_data || []).map((section) => ({
    id: section.id,
    title: section.title || "",
    description: section.description || "",
    items: (section.items || [])
      .map((item) => byKey[`${item.target_type}:${item.target_id}`])
      .filter(Boolean),
  }));
};

/**
 * Hydrates form-friendly milestones from the API row (unix due → date input).
 */
export const milestonesFromRoom = (room) => {
  return (room.field_milestones || []).map((milestone) => ({
    ...milestone,
    dueInput: milestone.due ? moment.unix(milestone.due).format("YYYY-MM-DD") : "",
  }));
};

/**
 * Builds the CRUD payload from form state. Emits both the sections layout
 * (JSON structure) and the deduped flat field_references index the backend
 * keeps for referential integrity.
 */
export const buildRoomPayload = (form) => {
  const references = [];
  const seen = {};

  const sections = form.sections.map((section) => ({
    id: section.id,
    title: section.title,
    description: section.description,
    items: section.items.map((item) => {
      const type = item.type || item.bundle;
      const key = `${type}:${item.id}`;
      if (!seen[key]) {
        seen[key] = true;
        references.push({ bundle: type, id: item.id });
      }
      return { target_type: type, target_id: item.id };
    }),
  }));

  const payload = {
    title: form.title,
    summary: form.description,
    field_company: form.company,
    room_status: form.status,
    field_expiry: form.expiry
      ? Math.floor(new Date(form.expiry).getTime() / 1000)
      : 0,
    field_branding_colour: form.colour,
    field_branding_logo: form.logoFid ? { target_id: form.logoFid } : null,
    field_contacts: form.contacts.filter(
      (contact) => contact.name || contact.email
    ),
    field_sections_data: sections,
    field_references: references,
    field_intro_video: form.introVideoMid || null,
    field_alert_mode: form.alertMode,
    field_daily_digest: !!form.dailyDigest,
    field_access_mode: form.accessMode || "link",
    is_template: !!form.isTemplate,
    field_milestones: form.milestones
      .filter((milestone) => milestone.title)
      .map((milestone) => ({
        id: milestone.id,
        title: milestone.title,
        description: milestone.description || "",
        due: milestone.dueInput
          ? Math.floor(new Date(milestone.dueInput).getTime() / 1000)
          : 0,
        assignee: milestone.assignee === "buyer" ? "buyer" : "seller",
        status: milestone.status === "done" ? "done" : "pending",
      })),
  };

  // Only send the password when the seller actually changed it - the
  // backend treats absence as "keep", empty string as "remove".
  if (form.passwordDirty) {
    payload.field_password = form.password || "";
  }

  return payload;
};

export default function SalesRoomForm(props) {
  const { form, setForm } = props;

  // Which media picker is open: 'logo' | 'video' | null.
  const [mediaPicker, setMediaPicker] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const canManageTemplates =
    permissions?.manage_sales_room_templates === true;

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateContact = (index, field, value) => {
    const contacts = form.contacts.map((contact, i) =>
      i === index ? { ...contact, [field]: value } : contact
    );
    setField("contacts", contacts);
  };

  const subtitle = (text) => (
    <Typography owner="admin" variant="h6" className="ss-subtitle mb-2">
      {text}
    </Typography>
  );

  return (
    <div className="ps-modal sales-room-form-panel">
      <div className="ps-modal-content">
        <Container>
          {/* Fields are paired row-by-row (not two independent stacks) so
              helper text under one field only grows its own row - labels on
              the left and right always start level. */}
          <Row>
            <Col xs={12} md={6}>
              <Box className="ls">
                <div className="form-group">
                  <label htmlFor="room-title">Room name*</label>
                  <TextField
                    required
                    id="room-title"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={form.title}
                    sx={inputSx}
                    placeholder="e.g. Acme Corp - Q2 Proposal"
                    onInput={(e) => setField("title", e.target.value)}
                  />
                </div>
              </Box>
            </Col>
            <Col xs={12} md={6}>
              <Box className="rs">
                <div className="form-group">
                  <label htmlFor="roomStatus">Status*</label>
                  <CategorySelector
                    name="roomStatus"
                    label="Status"
                    categories={ROOM_STATUSES}
                    selectedCategory={ROOM_STATUSES.find(
                      (status) => status.value === form.status
                    )}
                    outlined={true}
                    variant="outlined"
                    size="small"
                    handleCategorySelection={(value) =>
                      setField("status", value)
                    }
                  />
                </div>
              </Box>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Box className="ls">
                <div className="form-group">
                  <label htmlFor="room-company">Company</label>
                  <TextField
                    id="room-company"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={form.company}
                    sx={inputSx}
                    placeholder="Prospect company name"
                    onInput={(e) => setField("company", e.target.value)}
                  />
                </div>
              </Box>
            </Col>
            <Col xs={12} md={6}>
              <Box className="rs">
                <div className="form-group">
                  <label htmlFor="room-expiry">Expires</label>
                  <TextField
                    id="room-expiry"
                    className="date-input-field"
                    type="datetime-local"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={form.expiry}
                    onChange={(e) => setField("expiry", e.target.value)}
                    helperText="Leave empty for no expiry"
                  />
                </div>
              </Box>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Box className="ls">
                <div className="form-group">
                  <label htmlFor="room-description">
                    Description for the buyer
                  </label>
                  <TextField
                    id="room-description"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={form.description}
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: 45,
                        paddingTop: "3px",
                        paddingBottom: "3px",
                      },
                    }}
                    multiline
                    maxRows={3}
                    onInput={(e) => setField("description", e.target.value)}
                  />
                </div>
              </Box>
            </Col>
            <Col xs={12} md={6}>
              <Box className="rs">
                <div className="form-group">
                  <label htmlFor="roomAlerts">Visit alerts</label>
                  <CategorySelector
                    name="roomAlerts"
                    label="Visit alerts"
                    categories={ALERT_MODES}
                    selectedCategory={ALERT_MODES.find(
                      (mode) => mode.value === form.alertMode
                    )}
                    outlined={true}
                    variant="outlined"
                    size="small"
                    handleCategorySelection={(value) =>
                      setField("alertMode", value)
                    }
                  />
                  <FormControlLabel
                    className="mt-1"
                    control={
                      <Checkbox
                        size="small"
                        checked={!!form.dailyDigest}
                        onChange={(e) =>
                          setField("dailyDigest", e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography owner="admin" variant="body2">
                        Daily digest email (only on days with activity)
                      </Typography>
                    }
                  />
                </div>
              </Box>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Box className="ls">
                <div className="form-group">
                  <label htmlFor="roomAccess">Link access</label>
                  <CategorySelector
                    name="roomAccess"
                    label="Link access"
                    categories={ACCESS_MODES}
                    selectedCategory={ACCESS_MODES.find(
                      (mode) => mode.value === (form.accessMode || "link")
                    )}
                    outlined={true}
                    variant="outlined"
                    size="small"
                    handleCategorySelection={(value) =>
                      setField("accessMode", value)
                    }
                  />
                </div>
              </Box>
            </Col>
            <Col xs={12} md={6}>
              <Box className="rs">
                {form.accessMode === "password" && (
                  <div className="form-group">
                    <label htmlFor="room-password">Room passcode</label>
                    <TextField
                      id="room-password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      fullWidth
                      size="small"
                      autoComplete="new-password"
                      value={form.password || ""}
                      sx={inputSx}
                      placeholder="No passcode"
                      helperText="Auto-generated for new rooms. Share it with your buyer separately from the room link."
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              title={showPassword ? "Hide passcode" : "Show passcode"}
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                          passwordDirty: true,
                        }))
                      }
                    />
                  </div>
                )}
                {form.accessMode === "restricted" && (
                  <div className="form-group">
                    <label
                      className="d-none d-md-block"
                      style={{ visibility: "hidden" }}
                    >
                      Invited people
                    </label>
                    <Typography owner="admin" variant="body2">
                      Buyers verify their email with a secure link we send to
                      their inbox - only people listed under{" "}
                      <strong>Buyer contacts</strong> below have access. Add or
                      remove contacts to manage who can enter, and use the
                      send icon next to a contact to (re-)send their access
                      link.
                    </Typography>
                  </div>
                )}
              </Box>
            </Col>
          </Row>
          {canManageTemplates && (
            <Row>
              <Col xs={12}>
                <div className="form-group">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!form.isTemplate}
                        onChange={(e) =>
                          setField("isTemplate", e.target.checked)
                        }
                      />
                    }
                    label={
                      <Typography owner="admin" variant="body2">
                        Save as template (visible to all sellers)
                      </Typography>
                    }
                  />
                </div>
              </Col>
            </Row>
          )}

          {/* Branding spans the full width as three even columns - no
              lopsided side-by-side sections. */}
          <Row className="mt-2">
            <Col xs={12}>{subtitle("Branding")}</Col>
            <Col xs={12} md={4}>
              <div className="form-group">
                <label htmlFor="room-colour">Accent colour</label>
                <div>
                  <TextField
                    id="room-colour"
                    type="color"
                    variant="outlined"
                    size="small"
                    className="sales-room-form__colour"
                    value={form.colour || "#0e6ba8"}
                    onChange={(e) => setField("colour", e.target.value)}
                  />
                </div>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="form-group">
                <label>Logo</label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {form.logoUrl && (
                    <img
                      className="sales-room-form__logo-preview"
                      src={
                        form.logoUrl.indexOf("http") === 0
                          ? form.logoUrl
                          : `${serverUrl}${form.logoUrl}`
                      }
                      alt="Room logo"
                    />
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none" }}
                    onClick={() => setMediaPicker("logo")}
                  >
                    {form.logoFid ? "Change logo" : "Select logo"}
                  </Button>
                  {form.logoFid && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: "none" }}
                      onClick={() => {
                        setField("logoFid", null);
                        setField("logoUrl", null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="form-group">
                <label>Intro video</label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none" }}
                    onClick={() => setMediaPicker("video")}
                  >
                    {form.introVideoMid ? "Change video" : "Select video"}
                  </Button>
                  {form.introVideoMid && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: "none" }}
                      onClick={() => {
                        setField("introVideoMid", null);
                        setField("introVideoName", null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <Typography
                  owner="admin"
                  variant="body2"
                  className="sales-room-form__intro-hint mt-1"
                >
                  {form.introVideoName ||
                    (form.introVideoMid
                      ? `media ${form.introVideoMid}`
                      : "A short personal introduction shown at the top of the buyer's room.")}
                </Typography>
              </div>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col xs={12}>
              {subtitle(
                form.accessMode === "restricted"
                  ? "Buyer contacts (access list)"
                  : "Buyer contacts"
              )}
              {form.contacts.map((contact, index) => (
                <Row className="gx-2 mb-2 align-items-center" key={`contact-${index}`}>
                  <Col xs={12} md={4}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={contact.name}
                      sx={inputSx}
                      placeholder="Name"
                      onInput={(e) => updateContact(index, "name", e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="email"
                      value={contact.email}
                      sx={inputSx}
                      placeholder="Email"
                      onInput={(e) => updateContact(index, "email", e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md={3}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={contact.role}
                      sx={inputSx}
                      placeholder="Role"
                      onInput={(e) => updateContact(index, "role", e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md={1} className="d-flex align-items-center">
                    {form.accessMode === "restricted" &&
                      props.onSendAccessLink &&
                      contact.email && (
                        <IconButton
                          size="small"
                          title="Send access link to this contact"
                          onClick={() => props.onSendAccessLink(contact.email)}
                        >
                          <SendLinkIcon fontSize="small" />
                        </IconButton>
                      )}
                    <IconButton
                      size="small"
                      title="Remove contact"
                      onClick={() =>
                        setField(
                          "contacts",
                          form.contacts.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Col>
                </Row>
              ))}
              <Button
                variant="outlined"
                size="small"
                sx={{ textTransform: "none" }}
                startIcon={<AddIcon />}
                onClick={() =>
                  setField("contacts", [
                    ...form.contacts,
                    { name: "", email: "", role: "" },
                  ])
                }
              >
                Add contact
              </Button>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xs={12}>
              {subtitle("Content sections")}
              <SectionBuilder
                sections={form.sections}
                setSections={(sections) => setField("sections", sections)}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xs={12}>
              {subtitle("Shared plan")}
              <Typography owner="admin" variant="body2" className="mb-2">
                Milestones appear in the buyer's room with a progress bar.
                Buyers can tick off items assigned to them.
              </Typography>
              <MilestoneBuilder
                milestones={form.milestones}
                setMilestones={(milestones) => setField("milestones", milestones)}
              />
            </Col>
          </Row>
        </Container>
      </div>

      {mediaPicker && (
        <MediaLibrary
          user={user}
          csrf_token={user.csrf_token}
          mediaType={mediaPicker === "logo" ? "image" : "video"}
          files={{ aspectRatio: null }}
          showMediaLibrary={!!mediaPicker}
          setShowMediaLibrary={(open) => {
            if (!open) {
              setMediaPicker(null);
            }
          }}
          onMediaInsert={(media) => {
            if (mediaPicker === "logo") {
              setForm((prev) => ({
                ...prev,
                logoFid: media.fid,
                logoUrl: media.field_media_image || prev.logoUrl,
              }));
            } else {
              setForm((prev) => ({
                ...prev,
                introVideoMid: media.mid || media.fid,
                introVideoName: media.name || media.filename || null,
              }));
            }
            setMediaPicker(null);
          }}
        />
      )}
    </div>
  );
}
